<template>
  <component :is="collectivesData === undefined ? 'div' : 'div'">

    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="collectivesData === undefined"
    >
      <h4 class="alert-heading">
        Error bij het ophalen van het collectief
      </h4>
      <div class="alert-body">
        Geen collectief gevonden voor deze id.
        <b-link
          class="alert-link"
          :to="{ name: 'app-collectives-list'}"
        >
          Collectieflijst
        </b-link>
        Voor andere collectieven
      </div>
    </b-alert>
    <template #title>
      <feather-icon
        icon="UserIcon"
        size="16"
        class="mr-0 mr-sm-50"
      />
      <span class="d-none d-sm-inline">Informatie</span>
    </template>

    <template v-if="collectivesData">
      <validation-observer
        #default="{ handleSubmit }"
        ref="refFormObserver"
      >
        <b-form
          class="mb-2"
          @submit.prevent="handleSubmit(updateOrganisation)"
        >
          <b-card>

            <div class="d-flex mb-1">
              <feather-icon
                icon="UserIcon"
                size="19"
              />
              <h4 class="mb-0 ml-50">
                Collectief
              </h4>
            </div>
            <b-row>

              <!-- Field: ID -->
              <b-col
                cols="12"
                md="2"
              >
                <b-form-group
                  label="ID"
                  label-for="id"
                >
                  <b-form-input
                    id="id"
                    v-model="collectivesData.id"
                    readonly="readonly"
                  />
                </b-form-group>
              </b-col>
              <!-- Field: Name -->
              <b-col
                cols="12"
                md="4"
              >
                <validation-provider
                  #default="validationContext"
                  name="name"
                  rules="required"
                >
                  <b-form-group
                    label="Naam"
                    label-for="name"
                  >
                    <b-form-input
                      id="name"
                      v-model="collectivesData.name"
                      :state="getValidationState(validationContext)"
                    />
                    <b-form-invalid-feedback :state="getValidationState(validationContext)">
                      {{ validationContext.errors[0] }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  label="Bedrijf"
                  label-for="company"
                >
                  <v-select
                    v-model="collectivesData.companyID"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="organisationOptions"
                    value="id"
                    label="name"
                    :reduce="organisation => organisation.id"
                    :clearable="false"
                    input-id="organisations"
                    disabled="disabled"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>
          <b-row>
            <b-col
              cols="12"
            >
              <b-card>
                <b-row>
                  <b-col
                    cols="6 text-left"
                  >
                    <b-button
                      variant="secondary"
                      class="mb-1 mb-sm-0 mr-0 mr-sm-1"
                      :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                      type="button"
                      :to="{ name: 'app-collectives-list' }"
                    >
                      <span>Terug</span>
                    </b-button>
                  </b-col>
                  <b-col
                    cols="6 text-right"
                  >
                    <b-button
                      v-if="$can('update', 'product')"
                      variant="primary"
                      class="mb-1 mb-sm-0 mr-0 mr-sm-1"
                      :block="$store.getters['app/currentBreakPoint'] === 'xs'"
                      type="submit"
                    >
                      <span>Opslaan</span>
                    </b-button>
                  </b-col>
                </b-row>
              </b-card>
            </b-col>
          </b-row>
        </b-form>
      </validation-observer>
    </template>

  </component>
</template>

<script>
import {
  BTab,
  BTabs,
  BCard,
  BAlert,
  BLink,
  BButton,
  BFormGroup,
  BFormInput,
  BForm,
  BFormTextarea,
  BRow,
  BCol,
  BFormInvalidFeedback,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import router from '@/router'
import { required } from '@validations'
import { avatarText } from '@core/utils/filter'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import formValidation from '@core/comp-functions/forms/form-validation'
import collectivesStoreModule from '@/views/pages/collectives/collectivesStoreModule'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import errorDefaultMessage from '@core/app-config/errorAndSuccesDefaultMessage'

export default {
  components: {
    BButton,
    BTab,
    BTabs,
    BCard,
    BAlert,
    BLink,
    BFormGroup,
    BFormInput,
    BForm,
    BFormTextarea,
    BRow,
    BCol,
    BFormInvalidFeedback,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      collectivesData: {
        id: null,
        name: '',
        companyID: '',
      },
      organisationOptions: [],
    }
  },
  mounted() {
    store.dispatch('app-collectives-edit/fetchCollective', { id: router.currentRoute.params.id })
      .then(response => {
        this.collectivesData = response.data
      })
      .catch(() => {
        this.collectivesData = undefined
      })
  },
  beforeMount() {
    this.getOrganisations()
  },
  methods: {
    getOrganisations() {
      store
        .dispatch('app-collectives-edit/fetchOrganisations', {
          sortBy: 'name',
          sortDesc: false,
        })
        .then(response => {
          this.organisationOptions = response.data.items
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            position: 'top-right',
            props: {
              title: 'Ophalen organisaties',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: `${errorDefaultMessage.fetchDataError}`,
            },
          })
        })
    },
    updateOrganisation() {
      this.collectivesData.id = router.currentRoute.params.id
      store.dispatch('app-collectives-edit/updateCollective', this.collectivesData)
        .then(() => {
          router.push({ name: 'app-collectives-list' })
        })
    },
  },
  setup() {
    const COLLECTIVES_EDIT_APP_STORE_MODULE_NAME = 'app-collectives-edit'

    // Register module
    if (!store.hasModule(COLLECTIVES_EDIT_APP_STORE_MODULE_NAME)) store.registerModule(COLLECTIVES_EDIT_APP_STORE_MODULE_NAME, collectivesStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(COLLECTIVES_EDIT_APP_STORE_MODULE_NAME)) store.unregisterModule(COLLECTIVES_EDIT_APP_STORE_MODULE_NAME)
    })

    const {
      refFormObserver,
      getValidationState,
    } = formValidation()

    return {
      refFormObserver,
      getValidationState,
      avatarText,
      required,
    }
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
